import { useRef, useState } from "react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { GeneralMedia } from "./GeneralMedia";
import { Column } from "@/components";
import { AddMediaMenu } from "@/components/upload-media/AddMediaMenu";
import { VideoLinkDialog } from "@/components/upload-media/VideoLinkDialog";
import { Label } from "@/components/ui/label";

import { useCreateCompanyFile } from "@/hooks/mutations/useCreateCompanyFile";
import { useDeleteCompanyFile } from "@/hooks/mutations/useDeleteCompanyFile";
import { MediaLink, MediaLinkType } from "@/types";

export const GeneralMediaContainer = <T extends Record<string, unknown>>({
  fieldName,
  form,
  onSave,
}: {
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  onSave: () => void;
}) => {
  /* Refs */
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* State */
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  /* Mutations */
  const { mutate: createCompanyFile } = useCreateCompanyFile();
  const { mutate: deleteCompanyFile } = useDeleteCompanyFile();

  /* Helpers */
  const getMediaLinks = (): MediaLink[] =>
    form.getValues(fieldName) as MediaLink[];

  const saveMediaLinks = (updated: MediaLink[]) => {
    form.setValue(fieldName, updated as Parameters<typeof form.setValue>[1]);
    onSave();
  };

  /* Handlers */
  const handleAddPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    createCompanyFile(
      {
        createCompanyFileRequest: {
          companyFile: file,
          name: file.name.replace(/\.[^.]+$/, ""),
        },
      },
      {
        onSuccess: (companyFile) => {
          const newMediaLink: MediaLink = {
            id: crypto.randomUUID(),
            type: MediaLinkType.UPLOADED_IMAGE,
            title: null,
            url: URL.createObjectURL(file),
            companyFileId: companyFile.id,
          };
          saveMediaLinks([...getMediaLinks(), newMediaLink]);
        },
      },
    );

    // Reset input so the same file can be re-selected.
    e.target.value = "";
  };

  const handleAddVideo = () => {
    setVideoDialogOpen(true);
  };

  const handleVideoSubmit = (url: string) => {
    const newMediaLink: MediaLink = {
      id: crypto.randomUUID(),
      type: MediaLinkType.VIDEO_URL,
      title: null,
      url,
      companyFileId: null,
    };
    saveMediaLinks([...getMediaLinks(), newMediaLink]);
  };

  const handleDeleteMedia = (companyFileId: number | null, id: string) => {
    // Company file ID is only set for uploaded images and images need to be deleted from the server.
    if (companyFileId) {
      deleteCompanyFile(
        { companyFileId: companyFileId },
        {
          onSuccess: () => {
            saveMediaLinks(getMediaLinks().filter((m) => m.id !== id));
          },
        },
      );
    } else {
      // For videos, we don't have a company file ID, so we just remove the media link from the list.
      saveMediaLinks(getMediaLinks().filter((m) => m.id !== id));
    }
  };

  /* Derived */
  // Use watch (reactive) so the component re-renders when signed URLs are resolved.
  const mediaLinks = form.watch(fieldName) as MediaLink[];

  /* Render */
  const render = () => (
    <Column className="gap-2">
      <Label>Media</Label>
      <GeneralMedia mediaLinks={mediaLinks} onDelete={handleDeleteMedia} />
      <AddMediaMenu onAddPhoto={handleAddPhoto} onAddVideo={handleAddVideo} />
      <input
        ref={fileInputRef}
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
        type="file"
      />
      <VideoLinkDialog
        onOpenChange={setVideoDialogOpen}
        onSubmit={handleVideoSubmit}
        open={videoDialogOpen}
      />
    </Column>
  );

  return render();
};
